<template>
  <section class="sbc-spa-banner">
    <NuxtImg
      format="webp"
      class="sbc-spa-banner__img"
      :src="spaCoverImg"
      alt="Lemon Spa & Wellness"
      loading="lazy"
      placeholder
    />
    <div class="sbc-spa-banner__scrim" aria-hidden="true"></div>
    <div class="sbc-spa-banner__content" v-reveal>
      <span class="sbc-spa-banner__kicker">{{ $t("spa_desc") }}</span>
      <h2 class="sbc-spa-banner__title">{{ $t("spa") }}</h2>
      <p class="sbc-spa-banner__desc">{{ generalStore().getVariable("spa_title") }}</p>
      <ul class="sbc-spa-banner__amenities">
        <li v-for="key in amenityKeys" :key="key">{{ $t(key) }}</li>
      </ul>
      <NuxtLink :to="redirect('spa')" class="sbc-spa-banner__cta" aria-label="Learn more about the spa">
        {{ $t("spa_about") }}
        <span class="sbc-spa-banner__cta-arrow" aria-hidden="true">&rarr;</span>
      </NuxtLink>
    </div>
  </section>
</template>

<script>
export default {
  name: "SpaBanner",
  data() {
    return {
      // Sauna, massage, pools, jacuzzi, gym, hairdresser — the fixed set of
      // spa facilities shown as tags here, instead of the CMS spa_title copy.
      amenityKeys: ["spa_3_title", "spa_2_title", "spa_5_title", "spa_6_title", "spa_7_title", "spa_8_title"],
      spaCoverImg: "https://api2.salamisbayconti.com/storage/spa2.webp",
    };
  },
  computed: {
    language() {
      return this.$i18n.locale;
    },
  },
  methods: {
    redirect(route) {
      if (this.language === 'en') {
        return `/en/${route}`;
      }
      return `/${route}`;
    },
  },
};
</script>

<style scoped>
.sbc-spa-banner {
  --sbc-navy-900: #0B1F2E;
  --sbc-gold-400: #D9B96B;
  --sbc-gold-600: #C19A45;
  position: relative;
  width: 100%;
  height: clamp(480px, 48vw, 680px);
  overflow: hidden;
  background: var(--sbc-navy-900);
}
.sbc-spa-banner__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.sbc-spa-banner__scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(270deg, rgba(11, 31, 46, .88) 0%, rgba(11, 31, 46, .58) 42%, rgba(11, 31, 46, .14) 72%);
}
.sbc-spa-banner__content {
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  width: 50%;
  margin-left: auto;
  align-items: flex-end;
  text-align: right;
  padding: clamp(28px, 6vw, 96px) clamp(28px, 6vw, 96px) clamp(28px, 6vw, 96px) clamp(48px, 9vw, 140px);
  /* Match the right edge of RoomSection's 1320px centred inner container. */
  padding-right: max(clamp(20px, 4vw, 48px), calc((100vw - 1320px) / 2));
  box-sizing: border-box;
}
.sbc-spa-banner__content[data-reveal="in"] {
  transition: translate 2100ms cubic-bezier(.2, .7, .2, 1), opacity 1700ms ease;
}
.sbc-spa-banner__content[data-reveal="out"] {
  translate: 160px 0;
  opacity: 0;
}
.sbc-spa-banner__kicker {
  font-family: 'Jost', sans-serif;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: .28em;
  text-transform: uppercase;
  color: var(--sbc-gold-400);
}
.sbc-spa-banner__title {
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-weight: 300;
  font-size: clamp(36px, 4.6vw, 66px);
  line-height: 1.1;
  letter-spacing: -0.01em;
  text-wrap: pretty;
  color: #FBF9F6;
  margin: 0;
}
.sbc-spa-banner__title em {
  font-style: italic;
  color: var(--sbc-gold-400);
}
.sbc-spa-banner__desc {
  font-family: 'Jost', sans-serif;
  font-weight: 300;
  font-size: clamp(1.05rem, 1.4vw, 1.25rem);
  line-height: 1.7;
  color: rgba(251, 249, 246, .82);
  margin: 0;
}
.sbc-spa-banner__amenities {
  display: flex;
  flex-wrap: nowrap;
  flex-shrink: 0;
  overflow-x: auto;
  gap: 6px;
  list-style: none;
  margin: 4px 0 0;
  padding: 0 0 4px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  justify-content: flex-end;
}
.sbc-spa-banner__amenities::-webkit-scrollbar {
  display: none;
}
.sbc-spa-banner__amenities li {
  font-family: 'Jost', sans-serif;
  font-size: .6875rem;
  font-weight: 600;
  letter-spacing: .03em;
  text-transform: uppercase;
  color: #FBF9F6;
  padding: 6px 12px;
  background: rgba(11, 31, 46, .45);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  border: 1px solid rgba(251, 249, 246, .5);
  border-radius: 999px;
  white-space: nowrap;
  flex: 0 0 auto;
}
.sbc-spa-banner__cta {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  width: fit-content;
  margin-top: 6px;
  background: #c9a978;
  color: #0d1b2a;
  padding: 16px 30px;
  font-family: 'Jost', sans-serif;
  font-size: .75rem;
  font-weight: 500;
  letter-spacing: .22em;
  text-transform: uppercase;
  text-decoration: none;
  transition: background 200ms ease;
}
.sbc-spa-banner__cta:hover {
  background: #f4ede1;
  color: #0d1b2a;
}
.sbc-spa-banner__cta-arrow {
  font-size: .9375rem;
  line-height: 1;
  transition: transform 200ms ease;
}
.sbc-spa-banner__cta:hover .sbc-spa-banner__cta-arrow {
  transform: translateX(3px);
}

@media (max-width: 767px) {
  .sbc-spa-banner {
    height: 560px;
  }
  .sbc-spa-banner__scrim {
    background:
      linear-gradient(90deg, rgba(11, 31, 46, .56) 0%, rgba(11, 31, 46, .36) 62%, rgba(11, 31, 46, .16) 100%),
      linear-gradient(180deg, rgba(11, 31, 46, .28) 0%, rgba(11, 31, 46, .62) 100%);
  }
  .sbc-spa-banner__content {
    width: 100%;
    justify-content: center;
    align-items: flex-start;
    gap: 12px;
    text-align: left;
    padding: 24px 20px;
  }
  .sbc-spa-banner__kicker {
    font-size: .6875rem;
    letter-spacing: .2em;
  }
  .sbc-spa-banner__title {
    font-size: clamp(2.25rem, 11vw, 3.25rem);
    line-height: 1.05;
  }
  .sbc-spa-banner__desc {
    max-width: 34rem;
    font-size: .9375rem;
    line-height: 1.55;
  }
  .sbc-spa-banner__amenities {
    flex-wrap: wrap;
    overflow-x: visible;
    justify-content: flex-start;
    max-width: 100%;
    gap: 5px;
  }
  .sbc-spa-banner__amenities li {
    font-size: .625rem;
    padding: 5px 10px;
  }
  .sbc-spa-banner__cta {
    align-self: flex-start;
    margin-top: 4px;
    padding: 13px 18px;
    font-size: .6875rem;
    letter-spacing: .14em;
  }
}
/* Mobile: +10px vertical padding (fixed-height section grows by the same 20px) */
@media (max-width: 767px) {
  .sbc-spa-banner { height: 580px; }
  .sbc-spa-banner__content { padding-block: 34px; }
}
</style>
